import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Dialog, FormControl, InputLabel, MenuItem, Select } from '@mui/material';
import Button from 'src/component/UI/Button';
import DeviceSelectorField from 'src/component/UI/DeviceSelectorField';
import { useTranslation } from 'src/i18n';
import { FIELD_REPORT_DEVICE_TYPE_MAP, FieldReportTypeDef } from './fieldReportTypes';

export default function FieldReportTypeSelectDialog (props) {
  const { open, onSelect, onClose, types, titleKey } = props;
  const { t } = useTranslation();
  const [selectedType, setSelectedType] = useState('');
  const [selectedDevice, setSelectedDevice] = useState(null);

  const getDeviceType = (reportType: string) => FIELD_REPORT_DEVICE_TYPE_MAP[reportType];

  // Reset on open rather than in handleConfirm
  useEffect(() => {
    if (open) {
      setSelectedType('');
      setSelectedDevice(null);
    }
  }, [open]);

  const handleTypeChange = (e) => {
    const newType = e.target.value;
    // Only clear the selected device when the new report type targets a different device type;
    // if the device type is unchanged the existing selection is still valid, so keep it.
    if (selectedType && getDeviceType(selectedType) !== getDeviceType(newType)) {
      setSelectedDevice(null);
    }
    setSelectedType(newType);
  };

  const handleConfirm = () => {
    if (selectedType) {
      onSelect(selectedType, selectedDevice ? selectedDevice.id : null);
    }
  };

  const handleClose = () => {
    setSelectedType('');
    setSelectedDevice(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth='sm' fullWidth>
      <h2 >{t(titleKey)}</h2>
      <div>
        <FormControl fullWidth margin='normal'>
          <InputLabel id='field-report-form-type-label' shrink>{t('fieldReport.formType')}</InputLabel>
          <Select
            labelId='field-report-form-type-label'
            value={selectedType}
            label={t('fieldReport.formType')}
            displayEmpty
            onChange={handleTypeChange}
          >
            <MenuItem value='' disabled>{t('fieldReport.selectReportType')}</MenuItem>
            {types.map(({ value, labelKey }: FieldReportTypeDef) => (
              <MenuItem key={value} value={value}>{t(labelKey)}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl fullWidth margin='normal'>
          <DeviceSelectorField
            type={selectedType ? getDeviceType(selectedType) : 'circuitsAndControllers'}
            onChange={setSelectedDevice}
            value={selectedDevice ? { label: selectedDevice.tag, value: selectedDevice.id } : null}
            disabled={!selectedType}
          />
        </FormControl>
      </div>
      <div className='button-bar'>
        <Button onClick={handleClose}>{t('common.cancel')}</Button>
        <Button cta onClick={handleConfirm} disabled={!selectedType}>
          {t('common.next')}
        </Button>
      </div>
    </Dialog>
  );
}

FieldReportTypeSelectDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onSelect: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  types: PropTypes.array.isRequired,
  titleKey: PropTypes.string.isRequired,
};
